/* eslint-disable @typescript-eslint/no-explicit-any */

import { BayLevelEnum, ForeAftEnum, IBayLevelData } from "open-vessel-definition";
import { IBayPattern, ISlotPattern } from "./types/IPositionPatterns";
import { pad3, sortByMultipleFields } from "@tedivo/tedivo-pure-helpers";

import { getSizesFromSlots } from "./getSizesFromSlots";

export function getAllThePairedBays(
  bls: IBayLevelData[],
  checkOnly40s: boolean,
): IGetPairedBaysOutput {
  const pairedBays: IPairedBays[] = [];
  const unpairedBays: IUnpairedBays[] = [];

  const has40Fn = (bl: IBayLevelData) => {
    return (Object.keys(bl.perSlotInfo || {}) as ISlotPattern[]).some((slot) =>
      Object.keys(bl.perSlotInfo?.[slot]?.sizes || {})
        .map(Number)
        .some((size) => size >= 40),
    );
  };

  const getDefinedSlots = (bl: IBayLevelData) => getSizesFromSlots(bl.perSlotInfo).definedSlots;

  const getMaxSize = (bl: IBayLevelData) => {
    const sizes = getSizesFromSlots(bl.perSlotInfo).sizes;
    return sizes.length ? Math.max(...sizes) : 0;
  };

  [BayLevelEnum.ABOVE, BayLevelEnum.BELOW].forEach((level) => {
    const bays = bls
      .filter((a) => a.level === level)
      .sort(sortByMultipleFields([{ name: "isoBay", ascending: true }]));

    for (let i = 0; i < bays.length; i++) {
      const bay = bays[i];

      let has40 = true;

      if (checkOnly40s) {
        has40 = has40Fn(bay as any);
      }

      if (bay.pairedBay === ForeAftEnum.AFT) {
        const nextBay = bays[i + 1];
        if (nextBay && nextBay.pairedBay === ForeAftEnum.FWD) {
          const baysPrefixes = new Set<IBayPattern>();
          for (let j = Number(bay.isoBay); j <= Number(nextBay.isoBay); j++) {
            baysPrefixes.add(pad3(j));
          }

          pairedBays.push({
            base: bay.isoBay,
            paired: nextBay.isoBay,
            has40: getMaxSize(bay as any) >= 40,
            level: level as BayLevelEnum.ABOVE | BayLevelEnum.BELOW,
            allBays: [bay.isoBay, nextBay.isoBay],
            baysPrefixes,
          });
          i++;
        } else {
          if (has40 || !checkOnly40s)
            unpairedBays.push({
              base: bay.isoBay,
              level: level as BayLevelEnum.ABOVE | BayLevelEnum.BELOW,
              has40: getMaxSize(bay as any) >= 40,
              allBays: [bay.isoBay],
              definedSlots: getDefinedSlots(bay as any),
            });
        }
      } else {
        if (has40 || !checkOnly40s)
          unpairedBays.push({
            base: bay.isoBay,
            level: level as BayLevelEnum.ABOVE | BayLevelEnum.BELOW,
            has40: getMaxSize(bay as any) >= 40,
            allBays: [bay.isoBay],
            definedSlots: getDefinedSlots(bay as any),
          });
      }
    }
  });

  const pairedBaysPer20Bay = [...pairedBays, ...unpairedBays].reduce((acc, pb) => {
    const [base, paired] = pb.allBays;
    const level = pb.level;

    if (!paired) {
      acc[`${base}-${level}`] = undefined;
    } else {
      acc[`${base}-${level}`] = paired;
      acc[`${paired}-${level}`] = base;
    }
    return acc;
  }, {} as Record<`${IBayPattern}-${BayLevelEnum}`, IBayPattern | undefined>);

  return { pairedBays, unpairedBays, pairedBaysPer20Bay };
}

export interface IGetPairedBaysOutput {
  pairedBays: IPairedBays[];
  unpairedBays: IUnpairedBays[];
  pairedBaysPer20Bay: Record<`${IBayPattern}-${BayLevelEnum}`, IBayPattern | undefined>;
}

export type IPairedBays = {
  base: IBayPattern;
  paired: IBayPattern;
  level: BayLevelEnum.ABOVE | BayLevelEnum.BELOW;
  has40: boolean;
  /** The base and paired bays */
  allBays: [IBayPattern, IBayPattern];
  /** A set that contains all the bays prefixes, including 40s. i.e. "001", "002", "003" */
  baysPrefixes: Set<IBayPattern>;
};

export type IUnpairedBays = {
  base: IBayPattern;
  level: BayLevelEnum.ABOVE | BayLevelEnum.BELOW;
  allBays: [IBayPattern];
  has40: boolean;
  definedSlots: number;
};
